import "./EmailLoginForm.scss";

import clsx from "clsx";
import { type FC } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Trans } from "react-i18next";

import { emailMustBeValid, emailMustHaveAtSign } from "../../helpers/password-errors";

import { PasswordForm, PasswordFormValue } from "components/PasswordForm";
import { ErrorMessageBanner } from "components/forms/ErrorMessageBanner";
import { t } from "i18n/i18n";
import { TrackedInput } from "mds/components/TrackedInput";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { LoadingDotsIcon } from "mds/icons";
import { Link } from "utils/routing";

export type AccountFormValue = PasswordFormValue & {
  email: string;
  first_name: string;
  last_name: string;
  eula: boolean;
  send_onboarding_email?: boolean;
};

interface AccountFormProps {
  onCancel?: () => void;
  onSubmit: (data: AccountFormValue) => Promise<void>;
  onShowLogin?: () => void;
  isLoading?: boolean;
  defaultValues?: Partial<AccountFormValue>;
  forAdding?: boolean;
}

export const AccountForm: FC<AccountFormProps> = ({
  onSubmit,
  onShowLogin,
  onCancel,
  isLoading = false,
  defaultValues,
  forAdding = false,
}) => {
  const form = useForm<AccountFormValue>({ defaultValues });

  const handleSubmit: SubmitHandler<AccountFormValue> = (formData) => {
    onSubmit(formData);
  };

  const labelClass = forAdding ? "body-s" : "body-m-bold";
  const fieldClass = forAdding
    ? "mds-text-field body-m w-full text-black-tint-20"
    : "email-login-form-input";

  const formEmail = form.watch("email");
  const formFirstName = form.watch("first_name");
  const isSubmitting = form.formState.isSubmitting || isLoading;

  return (
    <>
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="mb-3 flex w-full flex-col gap-3">
            <div>
              <label>
                <div className={labelClass}>
                  {forAdding ? t("user_info.user_email") : t("user_info.email")}
                </div>

                <TrackedInput
                  {...form.register("email", {
                    required: t("error.field_required"),
                    validate: {
                      emailMustHaveAtSign,
                      emailMustBeValid,
                    },
                  })}
                  aria-invalid={form.formState.errors.email ? "true" : "false"}
                  autoComplete="email"
                  className={fieldClass}
                  disabled={!!defaultValues}
                  eventName="Account - Sign up form / Email"
                  eventPropertyKey="userEmail"
                  id="email"
                  type="email"
                />
              </label>

              <ErrorMessageBanner className="body-s mt-1 text-wrap break-all" name="email" />
            </div>

            <div>
              <label>
                <div className={labelClass}>{t("user_info.first_name")}</div>

                <TrackedInput
                  {...form.register("first_name", {
                    required: t("error.field_required"),
                  })}
                  aria-invalid={form.formState.errors.first_name ? "true" : "false"}
                  autoComplete="given-name"
                  className={fieldClass}
                  eventName="Account - Sign up form / Given name"
                  eventPropertyKey="userGivenName"
                  id="first_name"
                  type="text"
                />
              </label>

              <ErrorMessageBanner className="body-s mt-1" name="first_name" />
            </div>

            <div>
              <label>
                <div className={`${labelClass} flex items-center justify-between`}>
                  <span>{t("user_info.last_name")}</span>
                  <span className="text-xs text-black-tint-40">{t("common.optional")}</span>
                </div>

                <TrackedInput
                  {...form.register("last_name")}
                  aria-invalid={form.formState.errors.last_name ? "true" : "false"}
                  autoComplete="family-name"
                  className={fieldClass}
                  eventName="Account - Sign up form / Family name"
                  eventPropertyKey="userFamilyName"
                  id="last_name"
                  type="text"
                />
              </label>

              <ErrorMessageBanner className="body-s mt-1" name="last_name" />
            </div>
          </div>

          {/* This invisible field is required for LastPass to recognize the autofill */}
          <input className="sr-only" type="submit" value="Signup" />

          {forAdding ? (
            <>
              <div className="mb-3">
                <label className="flex items-baseline gap-2" htmlFor="send_onboarding_email">
                  <TrackedInput
                    {...form.register("send_onboarding_email")}
                    aria-invalid={form.formState.errors.send_onboarding_email ? "true" : "false"}
                    eventName="Account - Add user form / Send onboarding email"
                    eventPropertyKey="fieldValue"
                    id="send_onboarding_email"
                    type="checkbox"
                  />

                  <div className="body-s">{t("admin.add_user.send_invitation_email")}</div>
                </label>

                <ErrorMessageBanner className="body-s mt-1" name="send_onboarding_email" />
              </div>

              <div
                className={clsx("flex items-center justify-end gap-2", forAdding ? "mt-8" : "mt-4")}
              >
                <SquareButton
                  disabled={isSubmitting}
                  eventName="Account - Add user / Cancel"
                  kind="tertiary"
                  size="m"
                  onClick={onCancel}
                >
                  {t("common.cancel")}
                  {isSubmitting && <LoadingDotsIcon />}
                </SquareButton>

                <SquareButton
                  disabled={!formEmail || !formFirstName || isSubmitting}
                  eventName="Account - Add user"
                  kind="accent"
                  size="m"
                  type="submit"
                >
                  {t("admin.add_user.add_user")}
                  {isSubmitting && <LoadingDotsIcon />}
                </SquareButton>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3 flex w-full flex-col gap-3">
                <PasswordForm
                  eventPrefix="Account - Sign up form / "
                  isNewPassword
                  useEmailLoginFormStyling
                />
              </div>

              <div>
                <label className="flex items-baseline gap-2" htmlFor="eula">
                  <TrackedInput
                    {...form.register("eula", {
                      required: t("user_options_menu.redeem.eula_required"),
                    })}
                    aria-invalid={form.formState.errors.eula ? "true" : "false"}
                    eventName="Account - Sign up form / Terms Agreed"
                    eventPropertyKey="eulaAccepted"
                    id="eula"
                    type="checkbox"
                  />

                  <div>
                    <Trans i18nKey="user_info.eula">
                      I agree to the
                      <Link
                        className="text-blue-shade-20"
                        target="_blank"
                        to="/terms-of-service-en"
                      >
                        Terms of Service
                      </Link>
                      and
                      <Link className="text-blue-shade-20" target="_blank" to="/privacy-policy-en">
                        Privacy Policy
                      </Link>
                    </Trans>
                  </div>
                </label>

                <ErrorMessageBanner className="body-s mt-1" name="eula" />
              </div>

              <SquareButton
                className="mt-4"
                disabled={!formEmail || !formFirstName || isSubmitting}
                eventName="Account - Confirm sign up"
                kind="accent"
                size="m"
                type="submit"
              >
                {defaultValues ? t("user_info.set_up_account") : t("user_info.sign_up")}
                {isSubmitting && <LoadingDotsIcon />}
              </SquareButton>
            </>
          )}
        </form>
      </FormProvider>

      {onShowLogin && (
        <div className="mt-8">
          {t("user_options_menu.redeem.already_have_account")}

          <SquareButton
            className="mt-4"
            eventName="Account - Sign in"
            kind="tertiary"
            size="m"
            onClick={onShowLogin}
          >
            {t("user_info.sign_in")}
          </SquareButton>
        </div>
      )}
    </>
  );
};
